import React, { useState, useRef, useEffect } from "react";
import { ErrorMessage, useField, useFormikContext } from "formik";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import styled, { css } from "styled-components";
import { formErrorMessage } from "./FormErrorMessage";
import { formatDateDefault } from "../../utils/helper";
import { useTheme } from "../../middleware/ThemeContext";

interface FormikDateRangeProps {
  name: string;
  placeholderStart?: string;
  placeholderEnd?: string;
  storeAsString?: boolean;
  style?: React.CSSProperties;
}

const Wrapper = styled.div`
  position: relative;
  display: flex;
  gap: 8px;
  flex-direction: column;
`;

const InputWrapper = styled.div<{ hasError?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;

  ${(props) =>
    props.hasError &&
    css`
      input {
        border-color: red;
      }
    `}
`;

const Input = styled.input`
  padding: 8px 10px;
  font-size: 14px;
  // border: 1px solid #ccc;
  border: 1px solid ${({ theme }) => theme.CreateFormTheme.colors.inputBorders};
  background-color: ${({ theme }) => theme.CreateFormTheme.colors.inputBackground};
  color: ${({ theme }) => theme.CreateFormTheme.colors.inputColor};
  border-radius: 6px;
  width: 100%;
  font-family: "Raleway", sans-serif;
  cursor: pointer;
`;


const CalendarPopup = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 8px;
  display: flex;
  gap: 16px;
  background: ${({theme}) => theme.CorporateTheme.colors.background};
  padding: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 999;

    .react-datepicker {
      background-color: ${({theme}) => theme.CreateFormTheme.colors.background};
      border: 1px solid ${({ theme }) => theme.CreateFormTheme.colors.inputBorders};
      color: color: ${({theme}) => theme.CorporateTheme.colors.textPrimary};
      
      .react-datepicker__header {
        background-color: ${({theme}) => theme.CorporateTheme.colors.background};

        .react-datepicker__current-month, .react-datepicker-time__header, .react-datepicker-year-header {
          color: ${({theme}) => theme.CorporateTheme.colors.textPrimary};
        }
      }

      .react-datepicker__day-name, .react-datepicker__day, .react-datepicker__time-name {
        color: ${({theme}) => theme.CorporateTheme.colors.textPrimary};
      }

      .react-datepicker__day:not([aria-disabled=true]):hover, .react-datepicker__month-text:not([aria-disabled=true]):hover, .react-datepicker__quarter-text:not([aria-disabled=true]):hover, .react-datepicker__year-text:not([aria-disabled=true]):hover {
        background-color: ${({ theme }) => theme.CorporateTheme.colors.labelBG};
      }
      .react-datepicker__day--keyboard-selected, .react-datepicker__month-text--keyboard-selected, .react-datepicker__quarter-text--keyboard-selected, .react-datepicker__year-text--keyboard-selected {
        background-color: ${({ theme }) => theme.CorporateTheme.colors.labelBG};
      }
    }
`;

const parseDate = (value: unknown): Date | null => {
  if (!value) return null;
  const date = new Date(value as string | Date);
  return isNaN(date.getTime()) ? null : date;
};

const FormikDateRange: React.FC<FormikDateRangeProps> = ({
  name,
  placeholderStart = "Start Date",
  placeholderEnd = "End Date",
  storeAsString = false,
  style,
}) => {
  const {theme} = useTheme();
  const ref = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);
  const { setFieldValue } = useFormikContext();
  const [field, meta] =
    useField<[Date | string | null, Date | string | null]>(name);

  const rawStart = field.value?.[0] ?? null;
  const rawEnd = field.value?.[1] ?? null;

  const startDate = parseDate(rawStart);
  const endDate = parseDate(rawEnd);

  const handleClickOutside = (e: MouseEvent) => {
    if (ref.current && !ref.current.contains(e.target as Node)) {
      setShow(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const updateFormikValue = (newStart: Date | null, newEnd: Date | null) => {
    const value = storeAsString
      ? [newStart?.toISOString() || null, newEnd?.toISOString() || null]
      : [newStart || null, newEnd || null];
    setFieldValue(name, value);
  };


  return (
    <Wrapper style={style} ref={ref}>
      <InputWrapper hasError={!!meta.touched && !!meta.error}>
        <Input
          theme={theme}
          readOnly
          min={0}
          onClick={() => setShow(!show)}
          value={formatDateDefault(startDate)}
          placeholder={placeholderStart}
        />
        <span>-</span>
        <Input
          theme={theme}
          readOnly
          min={0}
          onClick={() => setShow(!show)}
          value={formatDateDefault(endDate)}
          placeholder={placeholderEnd}
        />
      </InputWrapper>

      {meta.touched && meta.error && (
        <ErrorMessage name={name} render={formErrorMessage} />
      )}

      {show && (
        <CalendarPopup theme={theme}>
          <DatePicker
            selected={startDate}
            onChange={(date) => updateFormikValue(date, endDate)}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            inline
          />
          <DatePicker
            selected={endDate}
            onChange={(date) => updateFormikValue(startDate, date)}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            minDate={startDate || undefined}
            inline
          />
        </CalendarPopup>
      )}
    </Wrapper>
  );
};

export default FormikDateRange;
